/**
 * The header breadcrumb trail (plan 03 CHG-18), pure and alias-free for
 * `npm run test:unit`. Always rooted at the Command Briefing.
 *
 * Sandbox QA defect 2: a driver record showed "Seed rcp angela", a label made
 * from the URL id. Record ids never become words now: the record page supplies
 * its own label (the driver's name), and until it has loaded the crumb is the
 * neutral label for the record kind ("Driver record").
 */
import { isIdSegment } from './page-title.ts'

export interface Crumb {
  label: string
  href: string
}

/** Labels for a record id under its parent segment, so a crumb never shows a raw id. */
export const ID_LABELS: Record<string, string> = {
  drivers: 'Driver record',
  panel: 'Panel interview',
  workbench: 'Case',
}

/** Parent segments whose next segment is always a record id, whatever it looks like. */
const RECORD_PARENTS: ReadonlySet<string> = new Set(['drivers'])

export function breadcrumbTrail(
  pathname: string | null | undefined,
  options: {
    rootLabel: string
    labelOf: (segment: string) => string
    pathsWithoutPage: ReadonlySet<string>
    /** The record's own label (e.g. the driver's name), when the page has supplied it. */
    recordLabel?: string | null
  }
): Crumb[] {
  const crumbs: Crumb[] = [{ label: options.rootLabel, href: '/' }]
  const segments = (pathname ?? '/').split('/').filter(Boolean)
  let current = ''
  let parent = ''
  segments.forEach((segment, index) => {
    current += `/${segment}`
    const isRecord = RECORD_PARENTS.has(parent) || isIdSegment(segment)
    const isLast = index === segments.length - 1
    const label = isRecord
      ? (isLast && options.recordLabel?.trim()) || ID_LABELS[parent] || 'Record'
      : options.labelOf(segment)
    parent = segment
    if (options.pathsWithoutPage.has(current)) return
    crumbs.push({ label, href: current })
  })
  return crumbs
}
